"use client";

import { IUserFull } from "@gettactic/api";
import { Chip } from "@gettactic/components/src/data-display/chips/Chip/Chip";
import { UserChip } from "@gettactic/components/src/data-display/chips/UserChip/UserChip";
import { TextInput } from "@gettactic/components/src/form/TextInput";
import { AsyncUserSelectInput } from "@gettactic/components/src/form/UserSelectInput";
import { Button } from "@gettactic/components/src/primitives/button";
import { isGuestAttendee } from "@gettactic/helpers/src/workplace-requests/reservationFieldValue";
import { useUserListByIds } from "@gettactic/hooks";
import { Plus } from "lucide-react";
import React, { useMemo, useState } from "react";
import { isValidEmail } from "@/lib/validation/email";

interface ReservationAttendeesInputProps {
  /** User ids and guest addresses, mixed, as the answer holds them. */
  values: string[];
  onChange: (values: string[]) => void;
}

/**
 * Who to invite: people from the directory, and guests named by address.
 *
 * The list itself is the resting state — chips, and a button to add more —
 * because a request is usually filed against a list that is already right. The
 * pickers only appear once somebody asks for them.
 *
 * The two kinds are collected separately on purpose: the directory picker
 * searches server-side, which the whole organization needs, and it can only
 * ever return people who exist — while a guest is exactly the address of
 * somebody who doesn't.
 */
export const ReservationAttendeesInput: React.FC<
  ReservationAttendeesInputProps
> = ({ values, onChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [guestDraft, setGuestDraft] = useState("");

  const userIds = useMemo(
    () => values.filter((value) => !isGuestAttendee(value)),
    [values]
  );
  const guests = useMemo(() => values.filter(isGuestAttendee), [values]);

  const picked = useUserListByIds(userIds);
  const usersById = useMemo(() => {
    const byId = new Map<string, IUserFull>();
    for (const user of (picked.data ?? []).filter(Boolean) as IUserFull[]) {
      byId.set(user.id, user);
    }
    return byId;
  }, [picked.data]);

  const trimmedDraft = guestDraft.trim().toLowerCase();
  const canAddGuest =
    isValidEmail(trimmedDraft) && !guests.includes(trimmedDraft);

  const addGuest = () => {
    if (!canAddGuest) return;
    onChange([...values, trimmedDraft]);
    setGuestDraft("");
  };

  const remove = (value: string) =>
    onChange(values.filter((entry) => entry !== value));

  return (
    <div className="flex flex-col gap-2">
      <span className="form-field-label font-medium text-[13px] text-neutral-900">
        Attendees
      </span>

      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {/*
           * People are held back until their names are in: a chip is only worth
           * showing once it can say who it stands for. A guest is its own name.
           */}
          {picked.isLoading
            ? null
            : userIds.map((userId) => (
                <UserChip
                  key={userId}
                  user={usersById.get(userId) ?? { name: "Unknown user" }}
                  onCancel={() => remove(userId)}
                />
              ))}
          {guests.map((email) => (
            <Chip key={email} onCancel={() => remove(email)}>
              {email}
            </Chip>
          ))}
        </div>
      ) : null}

      {isAdding ? (
        <div className="flex flex-col gap-2">
          <AsyncUserSelectInput
            multiselect
            values={userIds}
            // The guests ride along untouched: the picker only ever speaks for
            // the people half of the list.
            onChangeValues={(nextUserIds: string[]) =>
              onChange([...nextUserIds, ...guests])
            }
            placeholder="Search people"
          />
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <TextInput
                type="email"
                value={guestDraft}
                onChange={(event) => setGuestDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  // Enter inside a form would submit the request instead.
                  event.preventDefault();
                  addGuest();
                }}
                placeholder="Or invite a guest: name@company.com"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              // Sized to the input beside it: at the default `sm` height it
              // sat 6px short and read as another chip rather than an action.
              className="h-[42px]"
              disabled={!canAddGuest}
              onClick={addGuest}
            >
              Add
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconLeft={<Plus className="h-4 w-4" />}
            onClick={() => setIsAdding(true)}
          >
            Add attendees
          </Button>
        </div>
      )}

      <p className="text-neutral-500 text-xs">
        Attendees are invited when the request is approved, not before.
      </p>
    </div>
  );
};
